import React from 'react';
import { Box, Typography } from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { colors } from '../../styles/theme';
import {
  BlockHeader,
  FORM_GROUP_TITLE_SX,
  LOT_INFO_FIELD_ROW_SX,
  LOT_INFO_ROW_INNER_SX,
  LOT_INFO_SECTION_BORDER,
  PUBLICATION_BLOCK_SX,
  lotInfoFieldLabelLayoutSx,
  lotInfoFieldValueSx,
} from '../../Map/components/DockPanel/Tabs/formBlockUi';
import {
  formatCadArea,
  formatCadPrice,
  getCadParcelAttachments,
  getCadParcelId,
  getCadParcelLabel,
  getCadParcelPhotos,
  getCadParcelVideos,
  getCadUseLabel,
} from '../utils/cadParcelDisplay';
import { getOrientationLabel } from '../../Utils/compassOrientation';
import CadStockStatusLabel from './CadStockStatusLabel';
import CadParcelImages from './CadParcelImages';
import ListingVideoEmbeds from '../../Map/components/DockPanel/Tabs/ListingVideoEmbeds';
import ListingAttachments from '../../Map/components/DockPanel/Tabs/ListingAttachments';
import ShowroomParcelPlanimetria from './ShowroomParcelPlanimetria';

const EMPTY_OMIT_GROUPS = [];

function displayValue(value) {
  if (value == null || value === '' || value === '—') return null;
  return value;
}

function FieldRow({ label, value }) {
  const shown = displayValue(value);
  return (
    <Box sx={{ ...LOT_INFO_FIELD_ROW_SX, ...LOT_INFO_ROW_INNER_SX }}>
      <Typography sx={lotInfoFieldLabelLayoutSx()}>{label}</Typography>
      <Typography sx={lotInfoFieldValueSx({ muted: shown == null })}>
        {shown == null ? '—' : shown}
      </Typography>
    </Box>
  );
}

function FieldGroup({ title, children }) {
  return (
    <Box>
      <Typography sx={FORM_GROUP_TITLE_SX}>{title}</Typography>
      <Box
        sx={{
          borderRadius: '8px',
          border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * Ficha de parcela CAD de solo lectura. Mismos grupos que el formulario
 * de stock (Presentación, Estado, Precio, Lote). Visitante de lista
 * compartida y `/#/lista/:id`.
 *
 * `omitGroups`: en vista branded se ocultan Presentación/Estado/Precio
 * porque van en BrandedListingContent. `lead` va en su propio cuadro
 * (mismo borde que Lote / Planimetría), arriba de los grupos restantes.
 * `omitGroups` no cambia la vista default (Terrio).
 */
export default function ShowroomParcelReadout({
  feature,
  omitGroups = EMPTY_OMIT_GROUPS,
  lead = null,
}) {
  const props = feature?.properties || {};
  const fos = props.fos == null || props.fos === '' ? null : String(props.fos);
  const fot = props.fot == null || props.fot === '' ? null : String(props.fot);
  const skip = (name) => omitGroups.includes(name);
  const photos = getCadParcelPhotos(feature);
  const videos = getCadParcelVideos(feature);
  const attachments = getCadParcelAttachments(feature);
  const showPhotos = !skip('fotos') && photos.length > 0;
  // Videos/adjuntos siempre en el GET showroom (sin toggle). Mostrar si vienen.
  const showVideos = !skip('videos') && videos.length > 0;
  const showAttachments = !skip('adjuntos') && attachments.length > 0;
  const showHeader = !lead;
  const hasFieldGroups = (
    !skip('presentacion')
    || !skip('estado')
    || !skip('precio')
    || !skip('lote')
    || showPhotos
    || showVideos
    || showAttachments
  );

  const fieldGroups = (
    <>
      {!skip('presentacion') && (
        <FieldGroup title="Presentación">
          <FieldRow label="Título" value={getCadParcelLabel(feature)} />
          <FieldRow label="Descripción" value={props.description} />
        </FieldGroup>
      )}

      {!skip('estado') && (
        <FieldGroup title="Estado">
          <FieldRow label="Estado" value={<CadStockStatusLabel status={props.stock_status} />} />
          <FieldRow label="Uso" value={getCadUseLabel(props.use)} />
        </FieldGroup>
      )}

      {!skip('precio') && (
        <FieldGroup title="Precio">
          <FieldRow label="Precio" value={formatCadPrice(props.price, props.currency)} />
        </FieldGroup>
      )}

      {!skip('lote') && (
        <FieldGroup title="Lote">
          <FieldRow label="Manzana" value={props.block} />
          <FieldRow label="Lote" value={props.name} />
          <FieldRow label="Superficie" value={formatCadArea(props.area_m2)} />
          <FieldRow label="Orientación" value={getOrientationLabel(props.orientation) || null} />
          <FieldRow label="Posición" value={props.position_label} />
          <FieldRow label="FOS" value={fos} />
          <FieldRow label="FOT" value={fot} />
        </FieldGroup>
      )}

      {showPhotos && (
        <Box>
          <Typography sx={FORM_GROUP_TITLE_SX}>Fotos</Typography>
          <CadParcelImages
            parcelId={getCadParcelId(feature)}
            canEdit={false}
            initialImages={feature?.properties?.photos}
            coverImage={feature?.properties?.cover_image}
            hideEmpty
            showCoverBadge={false}
          />
        </Box>
      )}

      {showVideos && (
        <Box>
          <Typography sx={FORM_GROUP_TITLE_SX}>Videos</Typography>
          <ListingVideoEmbeds videos={videos} />
        </Box>
      )}

      {showAttachments && (
        <Box>
          <Typography sx={FORM_GROUP_TITLE_SX}>Adjuntos</Typography>
          <ListingAttachments attachments={attachments} />
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {lead ? (
        <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5 }}>
          {lead}
        </Box>
      ) : null}

      {(showHeader || hasFieldGroups) && (
        <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {showHeader ? (
            <BlockHeader
              icon={<PublicOutlinedIcon sx={{ fontSize: 16, color: colors.textSecondary, mt: 0.15 }} />}
              title="Publicación"
              subtitle="Datos comerciales del lote."
            />
          ) : null}
          {fieldGroups}
        </Box>
      )}

      <ShowroomParcelPlanimetria feature={feature} />
    </Box>
  );
}
